/**
 * Central registry for upgrade prompt messages.
 *
 * Using a TypeScript enum for keys so callers pass a stable, discoverable
 * symbol instead of plain strings.
 */

import { INSIGHT_LIMIT, FREE_OST_LIMIT } from './pricingTiers';

export enum UpgradeMessageKey {
    ProjectLimit = 'project_limit',
    TreeLimit = 'tree_limit',
    FeatureLocked = 'feature_locked',
    NodeLimit = 'node_limit',
    InsightLimit = 'insight_limit',
    AiUsageLimit = 'ai_usage_limit',
}

export type UpgradeMessageParams = Record<string, string> | undefined;

export function getUpgradeMessage(key: UpgradeMessageKey, params?: UpgradeMessageParams): string {
    switch (key) {
        case UpgradeMessageKey.ProjectLimit:
            return 'Project limit reached. Free accounts can have a maximum of 2 projects.';

        case UpgradeMessageKey.NodeLimit:
            return 'Node limit reached. Free accounts can have a maximum of 100 nodes per project.';

        case UpgradeMessageKey.TreeLimit:
            return `Tree limit reached. Free accounts can have a maximum of ${FREE_OST_LIMIT} trees per project.`;

        case UpgradeMessageKey.InsightLimit:
            return `Insight limit reached. Free accounts can have a maximum of ${INSIGHT_LIMIT} insights per project.`;

        case UpgradeMessageKey.AiUsageLimit: {
            const used = params?.creditsUsed;
            const limit = params?.creditLimit;
            if (used && limit) {
                return `You've used your AI credits for this month (${used}/${limit}). It resets next month.`;
            }
            return "You've used your AI credits for this month. It resets next month.";
        }

        case UpgradeMessageKey.FeatureLocked: {
            const feature = params?.feature ?? 'This feature';
            const requiredTier = params?.requiredTier ?? 'a higher plan';
            return `"${feature}" requires the ${requiredTier} plan. Upgrade to unlock this feature.`;
        }

        default:
            return '';
    }
}

export const UPGRADE_MESSAGE_KEYS: UpgradeMessageKey[] = [
    UpgradeMessageKey.ProjectLimit,
    UpgradeMessageKey.TreeLimit,
    UpgradeMessageKey.FeatureLocked,
    UpgradeMessageKey.NodeLimit,
    UpgradeMessageKey.InsightLimit,
    UpgradeMessageKey.AiUsageLimit,
];
